/**
 * MergeCarriersPage
 *
 * Main page for carrier merger analysis feature.
 * Allows users to:
 * - Select multiple carriers
 * - Analyze potential merger/acquisition
 * - View combined BASIC scores
 * - Export analysis reports
 */

import React, { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { GitMerge, AlertTriangle, RefreshCw, History, ChevronRight } from 'lucide-react';
import { Navbar } from '../components/shared/Navbar';
import CarrierSelector, { SelectedCarrier } from '../components/merger/CarrierSelector';
import MergerAnalysisResults from '../components/merger/MergerAnalysisResults';
import { LoadingSpinner } from '../components/shared/LoadingSpinner';
import { mergerApi, MergerAnalysis } from '../services/merger.api';
import '../styles/modern.css';

const MergeCarriersPage: React.FC = () => {
  const navigate = useNavigate();

  // State
  const [selectedCarriers, setSelectedCarriers] = useState<SelectedCarrier[]>([]);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [analysisResult, setAnalysisResult] = useState<MergerAnalysis | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isExporting, setIsExporting] = useState(false);

  // Handle carriers selection change
  const handleCarriersChange = useCallback((carriers: SelectedCarrier[]) => {
    setSelectedCarriers(carriers);
    // Clear previous analysis when carriers change
    if (analysisResult) {
      setAnalysisResult(null);
    }
    setError(null);
  }, [analysisResult]);

  // Handle analyze merger
  const handleAnalyze = useCallback(async () => {
    if (selectedCarriers.length < 2) {
      setError('Please select at least 2 carriers to analyze.');
      return;
    }

    setIsAnalyzing(true);
    setError(null);

    try {
      const dotNumbers = selectedCarriers.map((c) => c.dotNumber);
      const result = await mergerApi.analyzeMerger(dotNumbers, 'acquisition', true);
      setAnalysisResult(result);

      // Scroll to results
      setTimeout(() => {
        const resultsSection = document.getElementById('analysis-results');
        if (resultsSection) {
          resultsSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      }, 100);
    } catch (err: unknown) {
      console.error('Merger analysis error:', err);
      const errorObj = err as { response?: { data?: { message?: string } }; message?: string };
      setError(
        errorObj.response?.data?.message ||
          errorObj.message ||
          'Failed to analyze merger. Please try again.'
      );
    } finally {
      setIsAnalyzing(false);
    }
  }, [selectedCarriers]);

  // Handle reset
  const handleReset = useCallback(() => {
    setSelectedCarriers([]);
    setAnalysisResult(null);
    setError(null);
  }, []);

  // Export handlers
  const handleExportPdf = useCallback(async () => {
    if (!analysisResult || !analysisResult.id) {
      setError('Analysis must be saved before exporting to PDF');
      return;
    }

    setIsExporting(true);
    try {
      // Call backend PDF export endpoint
      const token = localStorage.getItem('token');
      const baseURL = import.meta.env.VITE_API_BASE_URL || '';
      const response = await fetch(`${baseURL}/api/mergers/${analysisResult.id}/pdf`, {
        method: 'GET',
        headers: token ? { 'Authorization': `Bearer ${token}` } : {},
      });

      if (!response.ok) {
        throw new Error('Failed to generate PDF');
      }

      // Download the PDF
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `merger-analysis-${analysisResult.combined.dotNumbers.join('-')}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('PDF export error:', err);
      setError('Failed to export PDF. Please try again.');
    } finally {
      setIsExporting(false);
    }
  }, [analysisResult]);

  const handleExportCsv = useCallback(async () => {
    if (!analysisResult) return;

    setIsExporting(true);
    try {
      // Create CSV content
      const headers = ['Category', 'Before (%)', 'After (%)', 'Change (%)', 'Status'];
      const rows = Object.entries(analysisResult.combined.combinedBasics).map(
        ([category, data]) => [
          category,
          data.previousAvg?.toFixed(1) || 'N/A',
          data.percentile.toFixed(1),
          data.change.toFixed(1),
          data.status,
        ]
      );

      const csvContent = [
        headers.join(','),
        ...rows.map((row) => row.join(',')),
        '',
        `Risk Score,${analysisResult.riskAssessment.score}`,
        `Risk Level,${analysisResult.riskAssessment.level}`,
        `Recommendation,${analysisResult.riskAssessment.recommendation}`,
      ].join('\n');

      // Download CSV
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.download = `merger-analysis-${Date.now()}.csv`;
      link.click();
    } catch (err) {
      console.error('CSV export error:', err);
      setError('Failed to export CSV. Please try again.');
    } finally {
      setIsExporting(false);
    }
  }, [analysisResult]);

  const handleExportJson = useCallback(async () => {
    if (!analysisResult) return;

    setIsExporting(true);
    try {
      const jsonContent = JSON.stringify(analysisResult, null, 2);
      const blob = new Blob([jsonContent], { type: 'application/json' });
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.download = `merger-analysis-${Date.now()}.json`;
      link.click();
    } catch (err) {
      console.error('JSON export error:', err);
      setError('Failed to export JSON. Please try again.');
    } finally {
      setIsExporting(false);
    }
  }, [analysisResult]);

  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar />

      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Page Header */}
        <div className="mb-8 flex justify-between items-start flex-wrap gap-6">
          <div>
            <h1 className="heading-1 flex items-center gap-4">
              <GitMerge className="w-8 h-8" style={{ color: 'var(--color-primary)' }} />
              Merge Carriers Analysis
            </h1>
            <p className="text-muted mt-2 text-base">
              Analyze the impact of carrier mergers and acquisitions on combined
              safety performance
            </p>
          </div>

          {/* Action Buttons */}
          <div className="flex gap-2">
            {analysisResult && (
              <button
                onClick={handleReset}
                className="btn-secondary flex items-center gap-2"
              >
                <RefreshCw className="w-4 h-4" />
                New Analysis
              </button>
            )}
          </div>
        </div>

        {/* Info Card */}
        <div className="card-modern mb-6 border-l-4" style={{ borderLeftColor: 'var(--color-primary)' }}>
          <div className="flex gap-4 items-start">
            <GitMerge className="w-6 h-6 shrink-0" style={{ color: 'var(--color-primary)' }} />
            <div>
              <h4 className="text-base font-semibold text-gray-900 mb-2">
                Carrier Merger & Acquisition Analysis
              </h4>
              <p className="text-muted leading-relaxed text-sm">
                Predict what happens when multiple carriers merge. This tool
                calculates the combined entity&apos;s BASIC scores, identifies
                compliance risks, and provides recommendations for M&A
                decisions. Select 2-10 carriers by DOT number or company name to
                begin your analysis.
              </p>
            </div>
          </div>
        </div>

        {/* Error Message */}
        {error && (
          <div className="flex items-center gap-4 p-4 bg-red-50 border border-red-200 rounded-lg mb-6">
            <AlertTriangle className="w-6 h-6 text-red-800 shrink-0" />
            <div>
              <div className="font-semibold text-red-800 mb-1">
                Analysis Error
              </div>
              <p className="text-red-700 m-0 text-[0.9375rem]">
                {error}
              </p>
            </div>
            <button
              onClick={() => setError(null)}
              className="ml-auto bg-transparent border-none text-red-800 cursor-pointer text-xl p-1"
            >
              &times;
            </button>
          </div>
        )}

        {/* Carrier Selector */}
        <CarrierSelector
          selectedCarriers={selectedCarriers}
          onCarriersChange={handleCarriersChange}
          onAnalyze={handleAnalyze}
          isAnalyzing={isAnalyzing}
          minCarriers={2}
          maxCarriers={10}
        />

        {/* Loading State */}
        {isAnalyzing && (
          <div className="card-modern p-12 text-center">
            <LoadingSpinner size="lg" />
            <div className="mt-6 text-gray-600">
              <h3 className="text-xl font-semibold mb-2">
                Analyzing Merger...
              </h3>
              <p className="text-muted">
                Calculating combined BASIC scores and assessing risk factors.
                This may take a moment.
              </p>
            </div>
          </div>
        )}

        {/* Analysis Results */}
        {analysisResult && !isAnalyzing && (
          <div id="analysis-results">
            <MergerAnalysisResults
              analysis={analysisResult}
              onExportPdf={handleExportPdf}
              onExportCsv={handleExportCsv}
              onExportJson={handleExportJson}
              isExporting={isExporting}
            />
          </div>
        )}

        {/* Empty State - No Analysis Yet */}
        {!analysisResult && !isAnalyzing && selectedCarriers.length === 0 && (
          <div className="card-modern p-12 text-center border-dashed border-gray-300">
            <GitMerge className="w-16 h-16 text-gray-300 mx-auto mb-6" />
            <h3 className="text-xl font-semibold text-gray-600 mb-2">
              No Carriers Selected
            </h3>
            <p className="text-muted max-w-md mx-auto">
              Use the search above to find and add carriers. Once you&apos;ve
              selected at least 2 carriers, click "Analyze Merger" to see the
              combined safety impact.
            </p>
          </div>
        )}

        {/* Quick Links */}
        <div className="mt-12 pt-6 border-t border-gray-200">
          <h3 className="heading-3 mb-6 text-gray-600">
            Related Tools
          </h3>
          <div className="grid grid-cols-[repeat(auto-fit,minmax(280px,1fr))] gap-4">
            <button
              onClick={() => navigate('/dashboard')}
              className="card-modern text-left cursor-pointer flex items-center gap-4 transition-all hover:border-[var(--color-primary)]"
            >
              <div className="w-12 h-12 rounded-lg flex items-center justify-center shrink-0" style={{ backgroundColor: 'var(--color-primary)' }}>
                <GitMerge className="w-6 h-6 text-white" />
              </div>
              <div className="flex-1">
                <div className="font-semibold text-gray-900 mb-1">
                  Carrier Search
                </div>
                <div className="text-sm text-gray-500">
                  Look up individual carrier profiles and BASIC scores
                </div>
              </div>
              <ChevronRight className="w-5 h-5 text-gray-400" />
            </button>

            <button
              onClick={() => navigate('/monitored')}
              className="card-modern text-left cursor-pointer flex items-center gap-4 transition-all hover:border-[var(--color-primary)]"
            >
              <div className="w-12 h-12 rounded-lg bg-[#8b5cf6] flex items-center justify-center shrink-0">
                <History className="w-6 h-6 text-white" />
              </div>
              <div className="flex-1">
                <div className="font-semibold text-gray-900 mb-1">
                  Watchlist
                </div>
                <div className="text-sm text-gray-500">
                  Monitor carriers and receive alerts on changes
                </div>
              </div>
              <ChevronRight className="w-5 h-5 text-gray-400" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MergeCarriersPage;
